import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import SmartImage from "../SmartImage";
import images from "../../Constant/images";
import IconButton from "../IconButton";
import { FONTS } from "../../Constant/Theme";
import Colors from "../../Constant/Colors";
import { RegSmPri } from "../CustomStyles";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const DropdownMenu = styled.div`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  position: absolute;
  background-color: white;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  min-width: 160px;
  max-width: 200px;
  z-index: 1;
  border-radius: 4px;
  bottom: ${({ position }) => (position === "top" ? "100%" : "auto")};
  top: ${({ position }) => (position === "bottom" ? "100%" : "auto")};
  right: ${({ alignment }) => (alignment === "left" ? "0" : "auto")};
  left: ${({ alignment }) => (alignment === "right" ? "0" : "auto")};
  max-height: 250px;
  overflow: auto;
  margin: 5px;
`;

const DropdownItem = styled.div`
  color: black;
  padding: 12px 16px;
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }
`;

const DropdownFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
  border-top: 1px solid #ccc;
`;

const OkButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #0056b3;
  }
`;

const DropdownHeader = styled.div`
  padding: 8px 16px;
  font-weight: bold;
  border-bottom: 1px solid #ccc;
  background-color: ${Colors.lightGray};
`;

const Dropdown = ({
  items,
  icon,
  buttonText,
  buttonStyle,
  onItemClick,
  multiSelect = false,
  disabled,
  iconStyle,
  buttonTxtStyle,
  heading,
  noItems,
  onToggle,
  position, // prop for vertical positioning (optional)
  alignment, // prop for horizontal alignment (optional)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (onToggle) onToggle();
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      closeDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleItemSelection = (item) => {
    if (multiSelect) {
      setSelectedItems((prevSelectedItems) => {
        if (prevSelectedItems.includes(item)) {
          return prevSelectedItems.filter(
            (selectedItem) => selectedItem !== item
          );
        } else {
          return [...prevSelectedItems, item];
        }
      });
    } else {
      setSelectedItems([item]);
      onItemClick(item);
      closeDropdown();
    }
  };

  const isSelected = (item) => selectedItems.includes(item);

  const confirmSelection = () => {
    if (onItemClick) {
      onItemClick(selectedItems);
    }
    closeDropdown();
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {!buttonText && (
        <DropdownButton onClick={toggleDropdown}>
          <SmartImage
            src={icon ?? images.dots1}
            style={{
              width: 20,
              height: 20,
              transform: !icon ? "rotate(90deg)" : "",
              transition: "transform 150ms ease",
              ...iconStyle,
            }}
          />
        </DropdownButton>
      )}
      {buttonText && (
        <IconButton
          disabled={disabled}
          onClick={toggleDropdown}
          text={buttonText}
          style={buttonStyle}
          textStyle={buttonTxtStyle}
        />
      )}
      <DropdownMenu
        isOpen={isOpen}
        position={position || "bottom"} // default to "bottom" if position is not provided
        alignment={alignment || "right"} // default to "right" if alignment is not provided
      >
        {heading && <DropdownHeader>{heading}</DropdownHeader>}
        {items.length ? (
          items.map((item, index) => (
            <DropdownItem key={index} onClick={() => toggleItemSelection(item)}>
              <div>
                <p style={{ ...FONTS.Bold13, marginTop: -5 }}>
                  {item?.label ?? item?.username?.toUpperCase() ?? item?.title}
                </p>
                <p style={{ ...FONTS.Regular10, marginTop: -10 }}>
                  {item?.email ?? ""}
                </p>
              </div>
              {multiSelect && isSelected(item) && <span>✓</span>}
            </DropdownItem>
          ))
        ) : (
          <RegSmPri
            style={{ marginTop: 20, textAlign: "center", fontSize: 10 }}
          >
            {noItems}
          </RegSmPri>
        )}
        {multiSelect && (
          <DropdownFooter>
            <OkButton onClick={confirmSelection}>OK</OkButton>
          </DropdownFooter>
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default Dropdown;
